import dynamic from "next/dynamic";

// Static imports — used on homepage / most pages
import HeroContainer from "@/components/container/heroContainer";
import ImageDescContainer from "@/components/container/imageDescContainer";
import IntroContainer from "@/components/container/introContainer";
import PreLoader from "@/components/layout/preLoader";
import Brands from "@/components/container/brands";

// Dynamic imports — only parsed/compiled when the section is used
const Location = dynamic(() => import("@/components/contact/location"));
const Careers = dynamic(() => import("@/components/career/careers"));
const TextFloatImageContainer = dynamic(
    () => import("@/components/container/textFloatImageContainer"),
);
const VideoTestimonial = dynamic(
    () => import("@/components/container/videoTestimonial"),
);
const WorkList = dynamic(() => import("@/components/work/workList"));
const PinnedSectionBk = dynamic(
    () => import("@/components/container/pinnedSectionBk"),
);
const CardStack = dynamic(() => import("@/components/container/cardStack"));
const CaseStudiesGrid = dynamic(
    () => import("@/components/container/caseStudiesGrid"),
);
const PastAwards = dynamic(() => import("@/components/container/pastAwards"));
const AboutMap = dynamic(() => import("@/components/container/AboutMap"));
const News = dynamic(() => import("@/components/news/news"));
const Editor = dynamic(() => import("@/components/news/editor"));
const RelatedNews = dynamic(() => import("@/components/container/relatedNews"));
const ServicesOverview = dynamic(
    () => import("@/components/services/servicesOverview"),
);
const WorkBanner = dynamic(() => import("@/components/container/workBanner"));
const FullImageContainer = dynamic(
    () => import("@/components/container/fullImageContainer"),
);
const HorizontalScrollContainer = dynamic(
    () => import("@/components/container/horizontalScrollContainer"),
);
const WorkResult = dynamic(() => import("@/components/container/workResult"));
const AboutContiner = dynamic(
    () => import("@/components/container/aboutContiner"),
);
const AboutHumanising = dynamic(
    () => import("@/components/container/aboutHumanisingContainer"),
);
const CaseStudiesCardStack = dynamic(
    () => import("@/components/container/caseStudiesCardStack"),
);
const Works = dynamic(() => import("@/components/work/works"));
const ExperienceGrid = dynamic(
    () => import("@/components/experiences/experienceGrid"),
);
const Faqs = dynamic(() => import("@/components/container/Faqs"));
const NestedSlider = dynamic(
    () => import("@/components/container/nestedSlider"),
);
const ThreeIconCards = dynamic(
    () => import("@/components/card/threeIconCards"),
);
const ChapterAccordion = dynamic(
    () => import("@/components/container/accordianContainer"),
);
const ImageTextBannerWithLabel = dynamic(
    () => import("@/components/container/imageTextBannerContainer"),
);
const WorkflowScroll = dynamic(
    () => import("@/components/container/workflowScrollContainer"),
);
const ImageTextBannerWithSlider = dynamic(
    () => import("@/components/container/imageTextBannerWithSlider"),
);
const TabWithTableContent = dynamic(
    () => import("@/components/container/tabWithTableContent"),
);
const CSR = dynamic(() => import("@/components/container/csrContainer"));
const ExploreMoreServices = dynamic(
    () => import("@/components/container/exploreMoreServices"),
);
const PEPContainer = dynamic(
    () => import("@/components/container/PEPContainer"),
);
const ServicesSlider = dynamic(
    () => import("@/components/container/servicesSlider"),
);

export function sectionRenderer(block: any, index: number, path?: string) {
    switch (block.__component) {
        case "blocks.hero-banner":
            return <HeroContainer key={index} data={block} />;
        case "blocks.intro":
            return <IntroContainer key={index} data={block} />;
        case "blocks.image-text-banner":
            return (
                <ImageDescContainer
                    key={index}
                    data={block}
                    className="w-full lg:w-full"
                />
            );
        case "blocks.image-text-banner-custom":
            return <TextFloatImageContainer key={index} data={block} />;
        case "blocks.portfolio":
            return <WorkList key={index} data={block} />;
        case "blocks.locations":
            return <Location key={index} data={block} />;
        case "blocks.career":
            return <Careers key={index} data={block} />;
        case "blocks.video-testimonial":
            return <VideoTestimonial key={index} data={block} />;
        case "blocks.nested-slider":
            return <NestedSlider key={index} data={block} />;
        case "blocks.landing-banner":
            return <PreLoader key={index} data={block} />;
        case "blocks.dna":
            return <PinnedSectionBk key={index} data={block} />;
        case "blocks.past-project":
            // version is opt-in per block (shared.version component) — only
            // set on blocks that got the SG redesign. Anything else
            // (missing field, older content, other pages) falls back to the
            // MY card stack.
            return block.version?.version === "v2" ? (
                <CaseStudiesGrid key={index} data={block} />
            ) : (
                <CardStack key={index} data={block} />
            );
        case "blocks.brands":
            return <Brands key={index} data={block} />;
        case "blocks.past-awards":
            return <PastAwards key={index} data={block} />;
        case "blocks.about-banner":
            return <AboutMap key={index} data={block} />;
        case "blocks.news":
            return <News key={index} data={block} />;
        case "shared.editor":
            return <Editor key={index} data={block} path={path} />;
        case "blocks.related-news":
            return <RelatedNews key={index} data={block} />;
        case "blocks.services":
            return <ServicesOverview key={index} data={block} />;
        case "blocks.work-banner":
            return <WorkBanner key={index} data={block} />;
        case "blocks.image-bg-section":
            return <FullImageContainer key={index} data={block} />;
        case "blocks.work-approach-details":
            return <HorizontalScrollContainer key={index} data={block} />;
        case "blocks.work-result":
            return <WorkResult key={index} data={block} />;
        case "blocks.case-studies":
            return <CaseStudiesCardStack key={index} data={block} />;
        case "blocks.about-banner-with-video":
            return <AboutContiner key={index} data={block} video={true} />;
        case "blocks.about":
            return (
                <AboutHumanising
                    key={index}
                    data={block}
                    className="bg-lightPurpleToDarkPurple flex flex-col justify-center md:h-screen md:pb-[5.333vw] md:pt-[6.333vw]"
                />
            );
        case "blocks.works":
            return <Works key={index} data={block} />;
        case "blocks.experiences":
            return <ExperienceGrid key={index} data={block} />;
        case "blocks.faq":
            return <Faqs key={index} data={block} />;
        case "blocks.three-icon-cards":
            return <ThreeIconCards key={index} data={block} />;
        case "blocks.accordian-with-image":
            return <ChapterAccordion key={index} data={block} />;
        case "blocks.image-text-banner-with-label":
            return <ImageTextBannerWithLabel key={index} data={block} />;
        case "blocks.workflow-scroll":
            return <WorkflowScroll key={index} data={block} />;
        case "blocks.image-text-banner-with-slider":
            return <ImageTextBannerWithSlider key={index} data={block} />;
        case "blocks.tab-with-table-content":
            return <TabWithTableContent key={index} data={block} />;
        case "blocks.csr":
            return <CSR key={index} data={block} />;
        case "blocks.explore-more-services":
            return <ExploreMoreServices key={index} data={block} />;
        case "blocks.purpose-emotion-possibilities":
            return <PEPContainer key={index} data={block} />;
        case "blocks.services-slider":
            return <ServicesSlider key={index} data={block} />;
        default:
            return <></>;
    }
}
